/**
 * src/lib/generationSession.js — RC 1.57 Generation Session Guard.
 *
 * Unified generation-lifecycle / session-governance abstraction. A
 * "generation session" is the period between the moment a candidate
 * triggers a chargeable action (Analyse Fit, CV Variant, Cover
 * Letter, Interview Prep) and the moment the UI confirms it has
 * stably hydrated the resulting artefact.
 *
 * The operator regression of 14 May 2026 02:00 Qatar (RC 1.57 audit)
 * showed that tab-switches, remounts, and visibility changes can
 * mint FRESH chargeable lifecycles even though the user's intent
 * was a single click. Every chargeable action already had its own
 * idempotency key (via `lib/idempotencyKeys.js`) — but the key was
 * being CLEARED too aggressively at finalize-charge success, before
 * the frontend had a stable hydration. A subsequent visibility-driven
 * re-render that landed in the brief gap between "key cleared" and
 * "result bound" could mint a new key and dispatch a new chargeable
 * run.
 *
 * This module wraps `idempotencyKeys.js` with a stronger lifecycle:
 *
 *   1. `getOrMintSession(userId, jobId, action)` returns the current
 *      session if one is active (within TTL), or mints a new one.
 *      The session carries the idempotency key plus lifecycle flags
 *      (`hydrationStable`, `terminalSuccess`, `mintedAtMs`).
 *
 *   2. `markTerminalSuccess(userId, jobId, action)` is called when
 *      the chargeable POST (`/finalize-charge` for CV variant, the
 *      sync response for AF/CL/IP) returns a terminal-success
 *      envelope. The session enters "awaiting hydration".
 *
 *   3. `markHydrationStable(userId, jobId, action)` is called when
 *      the frontend has rendered the bound artefact AND confirmed
 *      it survives one full visibility/rebind cycle (typically the
 *      next paint after `setGeneratedResult(...)`). ONLY at this
 *      point does the session become eligible for natural decay.
 *
 *   4. `resetSession(userId, jobId, action)` is the EXPLICIT
 *      operator hook — called only by deliberate UI affordances
 *      such as a "Generate New Version" / "Re-analyse Fit" button
 *      that has SEPARATE confirm semantics. Never called by
 *      recovery, visibilitychange, popstate, or remount paths.
 *
 *   5. `isSessionActive(...)` is a READ-ONLY probe used by guards
 *      to short-circuit duplicate-dispatch eligibility. While a
 *      session is active, the existing idempotency key is reused
 *      verbatim → the backend's `credit_ledger.idempotency_key`
 *      partial unique index dedupes any duplicate POST at the DB.
 *
 * ──────────────────────────────────────────────────────────────────
 * HARD RAILS — strict-hold compliant (RC 1.57)
 * ──────────────────────────────────────────────────────────────────
 *   • NOT a billing surface.
 *   • NOT a schema change.
 *   • NOT a new endpoint.
 *   • Does NOT auto-dispatch anything.
 *   • Does NOT call generation endpoints during recovery.
 *   • Does NOT create alternate execution paths.
 *   • Recovery hooks remain READ-ONLY — they merely READ
 *     `isSessionActive` and `getActiveIdempotencyKey` to bind state.
 *
 * Backed by sessionStorage so a remount within `SESSION_TTL_MS`
 * keeps the lifecycle alive across React tree rebuilds, URL
 * rebinds, and tab-visibility cycles.
 */

import {
  getOrMintIdempotencyKey,
  clearIdempotencyKey,
  IDEMPOTENCY_TTL_MS,
} from './idempotencyKeys';

const PREFIX = 'ndor-gensession';

/** Session TTL — matches the upstream idempotency key TTL so a
 *  session can never outlive its key. */
export const SESSION_TTL_MS = IDEMPOTENCY_TTL_MS;

/** Post-success "stabilisation window" — once `markTerminalSuccess`
 *  fires, the session remains locked for this duration even if
 *  `markHydrationStable` is never called. Prevents a slow paint
 *  cycle or a tab-switch landing in the gap from minting a fresh
 *  session. 30 s is comfortably longer than any realistic React
 *  paint + setState chain. */
export const STABILISATION_WINDOW_MS = 30 * 1000;

function _key(userId, jobId, action) {
  return `${PREFIX}:${action}:${userId || 'anon'}:${jobId || ''}`;
}

function _read(userId, jobId, action) {
  try {
    const raw = sessionStorage.getItem(_key(userId, jobId, action));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.idempotencyKey !== 'string') return null;
    if (typeof parsed.mintedAtMs !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function _write(userId, jobId, action, payload) {
  try {
    sessionStorage.setItem(
      _key(userId, jobId, action),
      JSON.stringify(payload),
    );
  } catch {
    /* sessionStorage unavailable — fail-open. */
  }
}

/**
 * Return the active session if one exists AND is still within TTL.
 * Pure READ — never mutates state. Used by guards (e.g. CvSection's
 * mount-time rebind) to decide whether a remount is rebinding to an
 * existing session vs. eligible to mint a fresh one.
 */
export function getActiveSession(userId, jobId, action) {
  if (!action) return null;
  const s = _read(userId, jobId, action);
  if (!s) return null;
  const age = Date.now() - s.mintedAtMs;
  if (age >= SESSION_TTL_MS) return null;
  return s;
}

export function isSessionActive(userId, jobId, action) {
  return !!getActiveSession(userId, jobId, action);
}

/**
 * Return the active session's idempotency key, or null. Read-only.
 * Recovery / rebind paths use this to reuse the same key when they
 * fire an idempotent probe (e.g. CvSection's recovered
 * finalize-charge probe).
 */
export function getActiveIdempotencyKey(userId, jobId, action) {
  return getActiveSession(userId, jobId, action)?.idempotencyKey || null;
}

/**
 * Return the active session's idempotency key, or mint a fresh one
 * and create a new session. The session begins in `armed` state
 * (`hydrationStable=false`, `terminalSuccess=false`). The minted
 * key is delegated to `idempotencyKeys.getOrMintIdempotencyKey` so
 * the existing backend deduplication contract (`credit_ledger.
 * idempotency_key` partial unique index) is preserved verbatim.
 */
export function getOrMintSession(userId, jobId, action) {
  const existing = getActiveSession(userId, jobId, action);
  if (existing) return existing;
  const idempotencyKey = getOrMintIdempotencyKey(userId, jobId, action);
  const session = {
    idempotencyKey,
    mintedAtMs:        Date.now(),
    terminalSuccess:   false,
    terminalAtMs:      null,
    hydrationStable:   false,
    hydrationStableAt: null,
  };
  _write(userId, jobId, action, session);
  return session;
}

/**
 * Mark the chargeable POST as having returned terminal success.
 * The session is now LOCKED for at least `STABILISATION_WINDOW_MS`
 * regardless of subsequent visibility / remount events.
 */
export function markTerminalSuccess(userId, jobId, action) {
  const s = _read(userId, jobId, action);
  if (!s) return;
  s.terminalSuccess = true;
  s.terminalAtMs    = Date.now();
  _write(userId, jobId, action, s);
}

/**
 * Mark the frontend hydration / bind as stable. After this call
 * the session is free to decay naturally via TTL. This is the only
 * moment at which a non-explicit fresh generation could be allowed
 * — and even then ONLY after TTL expiry.
 */
export function markHydrationStable(userId, jobId, action) {
  const s = _read(userId, jobId, action);
  if (!s) return;
  s.hydrationStable   = true;
  s.hydrationStableAt = Date.now();
  _write(userId, jobId, action, s);
}

/**
 * EXPLICIT reset — called ONLY by deliberate UI affordances
 * (e.g. "Generate New Version" / "Re-analyse with fresh LLM run").
 *
 * Forbidden callers (per RC 1.57 brief):
 *   - visibilitychange listeners
 *   - popstate listeners
 *   - useArtefactRehydrate
 *   - URL-bound rebind effects
 *   - mount-time inflight recovery
 *   - any auto-fire path
 */
export function resetSession(userId, jobId, action) {
  if (!action) return;
  clearIdempotencyKey(userId, jobId, action);
  try {
    sessionStorage.removeItem(_key(userId, jobId, action));
  } catch {
    /* no-op */
  }
}

/**
 * Whether the session is in the "post-terminal-success
 * stabilisation window" — i.e. terminalSuccess was marked AND
 * we are still inside `STABILISATION_WINDOW_MS`. Used by the
 * dispatcher to decide whether to clear-then-mint vs. reuse.
 */
export function isWithinStabilisationWindow(userId, jobId, action) {
  const s = _read(userId, jobId, action);
  if (!s || !s.terminalSuccess || !s.terminalAtMs) return false;
  return (Date.now() - s.terminalAtMs) < STABILISATION_WINDOW_MS;
}

/**
 * Conditional clear — used by post-success handlers that want to
 * release the session ONLY when hydration is confirmed stable.
 * Operator-safe wrapper around `resetSession` that REFUSES to
 * release a session whose `hydrationStable` flag is still false
 * AND we are inside the stabilisation window.
 *
 * Returns `true` when the session was cleared, `false` when it
 * was deliberately preserved.
 */
export function releaseSessionIfStable(userId, jobId, action) {
  const s = _read(userId, jobId, action);
  if (!s) return true;
  if (!s.hydrationStable && isWithinStabilisationWindow(userId, jobId, action)) {
    return false;
  }
  resetSession(userId, jobId, action);
  return true;
}

export default {
  SESSION_TTL_MS,
  STABILISATION_WINDOW_MS,
  getActiveSession,
  isSessionActive,
  getActiveIdempotencyKey,
  getOrMintSession,
  markTerminalSuccess,
  markHydrationStable,
  resetSession,
  isWithinStabilisationWindow,
  releaseSessionIfStable,
};
